import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import apiService from '@/services/api';

const API = process.env.REACT_APP_BACKEND_URL;

export const ImportStudentsDialog = ({ open, onOpenChange, years, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [defaultYear, setDefaultYear] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    const res = await fetch(`${API}/api/students/import-template`, { headers: { Authorization: `Bearer ${apiService.getToken()}` } });
    if (!res.ok) { toast.error('Could not download template'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = 'student_import_template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) { toast.error('Choose an Excel or CSV file first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const data = await apiService.importStudents(file, defaultYear || null);
      setResult(data);
      if (data.created) toast.success(`${data.created} student${data.created === 1 ? '' : 's'} added`);
      else toast.warning('No new students were added');
      onImported?.();
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const close = (v) => { if (!v) { setFile(null); setResult(null); setDefaultYear(''); } onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-xl" data-testid="import-students-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" /> Upload Student List</DialogTitle>
          <DialogDescription>
            Excel or CSV with columns: <b>Student Name</b>, <b>Student ID</b>, <b>Date of Birth</b>, <b>Year</b>, Stream (optional), Parent Name (optional), <b>Parent Phone</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-2" data-testid="import-download-template">
            <Download className="h-4 w-4" /> Download template
          </Button>

          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="import-dropzone"
          >
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
            {file ? <p className="text-sm font-medium" data-testid="import-file-name">{file.name}</p>
                  : <p className="text-sm text-muted-foreground">Click to choose a file (.xlsx or .csv)</p>}
          </div>

          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">If the file has no Year column, use:</span>
            <select value={defaultYear} onChange={(e) => setDefaultYear(e.target.value)} className="h-9 border rounded-md px-2 bg-background" data-testid="import-default-year">
              <option value="">— per row —</option>
              {years.map((y) => <option key={y} value={y}>Year {y}</option>)}
            </select>
          </div>

          {result && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-sm" data-testid="import-result">
              <div className="flex gap-2 flex-wrap">
                <Badge className="bg-green-100 text-green-800">Added {result.created}</Badge>
                <Badge variant="secondary">Skipped {result.skipped}</Badge>
                <Badge className={result.errors ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}>Errors {result.errors}</Badge>
              </div>
              {result.parent_pin_hint && <p className="text-xs text-muted-foreground">{result.parent_pin_hint}</p>}
              {[...(result.skipped_rows || []), ...(result.error_rows || [])].length > 0 && (
                <ul className="max-h-36 overflow-auto text-xs space-y-0.5 text-muted-foreground">
                  {[...(result.skipped_rows || []), ...(result.error_rows || [])].map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="import-close">Close</Button>
          <Button onClick={upload} disabled={uploading || !file} className="bg-teal-700 hover:bg-teal-800" data-testid="import-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
